import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import formatMessage from 'format-message';
import { Select } from '@instructure/ui-select';
import { Tag } from '@instructure/ui-tag';
import { Spinner } from '@instructure/ui-spinner';
import { Text } from '@instructure/ui-text';
import { AccessibleContent } from '@instructure/ui-a11y-content';
import {
  useSearchDebounce,
  useSelectOptionsState,
  useCuratorOptions,
  useAnnouncements
} from './curator-list-select.hooks';

const CuratorListSelect = ({
  selectedCurators = [],
  searchResults = [],
  onAddCurator,
  onRemoveCurator,
  onSearchCurators,
  onResetSearch,
  isSearching = false
}) => {
  const inputRef = useRef(null);

  const { inputValue, handleInputChange: handleSearchInputChange, resetInputValue } = useSearchDebounce(
    onSearchCurators,
    onResetSearch
  );

  const { selectedOptions, availableOptions, getOptionById } = useCuratorOptions(
    selectedCurators,
    searchResults
  );

  const {
    isShowingOptions,
    highlightedOptionId,
    showOptions,
    hideOptions,
    highlightOption,
    resetHighlight,
    setHighlightedOptionId
  } = useSelectOptionsState(availableOptions);

  const { announcement, setAnnouncement } = useAnnouncements(
    availableOptions,
    highlightedOptionId,
    getOptionById,
    isSearching,
    inputValue
  );

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current.focus();
    }
  };

  const handleShowOptions = (event) => {
    showOptions();
    if (!inputValue && availableOptions.length > 0) {
      switch (event.key) {
        case 'ArrowDown':
          handleHighlightOption(event, { id: availableOptions[0].id });
          break;
        case 'ArrowUp':
          handleHighlightOption(event, {
            id: availableOptions[availableOptions.length - 1].id
          });
          break;
      }
    }
  };

  const handleHideOptions = (event) => {
    hideOptions();
  };

  const handleBlur = (event) => {
    resetHighlight();
  };

  const handleHighlightOption = (event, { id }) => {
    event.persist();
    const option = getOptionById(id);
    if (!option) return;

    highlightOption(id);
    setAnnouncement(option.label);
  };

  const handleSelectOption = (event, { id }) => {
    const option = getOptionById(id);
    if (!option) return;

    focusInput();

    if (onAddCurator) {
      onAddCurator(id);
    }

    resetHighlight();
    resetInputValue();
    hideOptions();

    if (onResetSearch) {
      onResetSearch();
    }

    setAnnouncement(formatMessage('{user} selected. List collapsed.', { user: option.label }));
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    handleSearchInputChange(value);
    showOptions();

    if (value === '' || value.length < 3) {
      if (availableOptions.length > 0) {
        setHighlightedOptionId(availableOptions[0].id);
      }
    } else {
      setAnnouncement(formatMessage('Searching for users...'));
    }
  };

  const handleKeyDown = (event) => {
    const backSpace = 8;
    const deleteKey = 46;
    if (event.keyCode === backSpace || event.keyCode === deleteKey) {
      if (inputValue === '' && selectedCurators.length > 0) {
        const lastCurator = selectedCurators[selectedCurators.length - 1];
        if (onRemoveCurator) {
          onRemoveCurator(lastCurator.uuid);
        }
        resetHighlight();
        const curatorName = `${lastCurator.firstName || ''} ${lastCurator.lastName || ''}`.trim();
        setAnnouncement(formatMessage('{user} removed', { user: curatorName }));
      }
    }
  };

  const dismissTag = (e, curatorId) => {
    e.stopPropagation();
    e.preventDefault();

    const curator = selectedCurators.find(c => c.uuid === curatorId);

    if (onRemoveCurator) {
      onRemoveCurator(curatorId);
    }

    resetHighlight();

    if (curator) {
      const curatorName = `${curator.firstName || ''} ${curator.lastName || ''}`.trim();
      setAnnouncement(formatMessage('{user} removed', { user: curatorName }));
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const renderTags = () => {
    return selectedOptions.map((option, index) => (
      <Tag
        data-automation={`curator-tag-${option.id}`}
        dismissible
        key={option.id}
        text={
          <AccessibleContent alt={formatMessage('Remove {user}', { user: option.label })}>
            {option.label}
          </AccessibleContent>
        }
        margin={index > 0 ? 'xxx-small xx-small xxx-small 0' : '0 xx-small 0 0'}
        onClick={(e) => dismissTag(e, option.id)}
      />
    ));
  };

  const getPlaceholderText = () => {
    if (isSearching) {
      return <Spinner renderTitle="Searching..." size="x-small" />;
    }
    if (inputValue.length > 0 && inputValue.length < 3) {
      return formatMessage('Type at least 3 characters to search');
    }
    if (inputValue.length >= 3 && availableOptions.length === 0) {
      return formatMessage('No users found');
    }
    return formatMessage('Type to search for users');
  };

  return (
    <div>
      <Select
        id="curator-list-select"
        renderLabel={
          <Text>
            {formatMessage('Add users to manage approved content')}
          </Text>
        }
        assistiveText={formatMessage(
          'Type at least 3 characters to search. Use arrow keys to navigate. Multiple selections allowed. Press backspace to remove last selected user.'
        )}
        inputValue={inputValue}
        isShowingOptions={isShowingOptions}
        inputRef={(el) => {
          inputRef.current = el;
        }}
        onBlur={handleBlur}
        onInputChange={handleInputChange}
        onRequestShowOptions={handleShowOptions}
        onRequestHideOptions={handleHideOptions}
        onRequestHighlightOption={handleHighlightOption}
        onRequestSelectOption={handleSelectOption}
        onKeyDown={handleKeyDown}
        renderBeforeInput={selectedOptions.length > 0 ? renderTags() : null}
      >
        {availableOptions.length > 0 ? (
          availableOptions.map((option) => (
            <Select.Option
              id={option.id}
              key={option.id}
              isHighlighted={option.id === highlightedOptionId}
            >
              {option.label}
            </Select.Option>
          ))
        ) : (
          <Select.Option id="empty-option" key="empty-option">
            {getPlaceholderText()}
          </Select.Option>
        )}
      </Select>
      {announcement && (
        <span role="alert" aria-live="assertive" style={{ position: 'absolute', left: '-10000px' }}>
          {announcement}
        </span>
      )}
    </div>
  );
};

CuratorListSelect.propTypes = {
  /** Array of currently selected curators */
  selectedCurators: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string.isRequired
    })
  ),
  /** Array of search result users */
  searchResults: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired
    })
  ),
  /** Callback when a curator is added - receives curator UUID */
  onAddCurator: PropTypes.func,
  /** Callback when a curator is removed - receives curator UUID */
  onRemoveCurator: PropTypes.func,
  /** Callback to search for curators - receives search string */
  onSearchCurators: PropTypes.func,
  /** Callback to reset search results */
  onResetSearch: PropTypes.func,
  /** Whether a search is currently in progress */
  isSearching: PropTypes.bool
};

export default CuratorListSelect;
